import type { RackingArea, SingularStationArea, Station } from '@thive/fddm-types';

import { getAreaColorByArea } from '@/modules/floorplanDesign/mappers/colors';
import {
  createLoadTypeDimensionsById,
  getSingularStationCountPerLane,
} from '@/modules/floorplanDesign/helpers/stationCapacity';

type StationArea = SingularStationArea | RackingArea;

export type AreaStationSummary = {
  id: 'stations' | 'rack-stations';
  kind: 'station' | 'rack';
  title: string;
  stationCount: number;
  areaCount: number;
  unitCount?: number;
  stationsPerUnit?: number | string;
};

export type SelectedAreaDetailsGroup = {
  id: string;
  name: string;
  color: string;
  areaCount: number;
  summaries: AreaStationSummary[];
};

type RackLevelDetails = {
  levelCount?: number;
  stationsPerLevel?: number | string;
};

type SingularAreaStationDetails = {
  stationCount: number;
  laneCount: number;
  stationsPerLane?: number;
};

const MILLIMETERS_PER_METER = 1000;

const isRackingArea = (area: StationArea): area is RackingArea => area.areaType === 'RACKING';

export const getUniqueStations = (stations: readonly Station[]): Station[] =>
  Array.from(
    new Map<string, Station>(
      stations.map((station): [string, Station] => [station.id, station]),
    ).values(),
  );

const getStationsForAreaIds = (
  stations: readonly Station[],
  areaIds: readonly string[],
): Station[] => {
  const areaIdSet = new Set(areaIds);

  return getUniqueStations(
    stations.filter((station) => station.areaIds.some((areaId) => areaIdSet.has(areaId))),
  );
};

const getLoadTypeTitle = (stations: readonly Station[], fallbackTitle: string): string => {
  const loadTypeIds = new Set(stations.flatMap((station) => station.loadTypeIds));

  if (loadTypeIds.size !== 1) return fallbackTitle;

  return loadTypeIds.values().next().value ?? fallbackTitle;
};

const capitalizeWord = (word: string): string => `${word.charAt(0).toUpperCase()}${word.slice(1)}`;

const getLoadHandlingTypeName = (
  loadHandlingType: SingularStationArea['loadHandlingType'],
): string => loadHandlingType.toLowerCase().split('_').map(capitalizeWord).join(' ');

const getRangeValue = (values: readonly number[]): number | string | undefined => {
  if (values.length === 0) return undefined;

  const minimumValue = Math.min(...values);
  const maximumValue = Math.max(...values);

  return minimumValue === maximumValue ? minimumValue : `${minimumValue}–${maximumValue}`;
};

const getOptionalRangeValue = (
  values: readonly (number | undefined)[],
): number | string | undefined => {
  const definedValues = values.filter((value): value is number => value !== undefined);
  if (definedValues.length !== values.length) return undefined;

  return getRangeValue(definedValues);
};

const getStationCountsByLevel = (rackStations: readonly Station[]): number[] => {
  const stationCountsByLevel = new Map<number, number>();

  rackStations.forEach((station) => {
    const levelInMillimeters = Math.round(station.cuboid.z * MILLIMETERS_PER_METER);
    const currentStationCount = stationCountsByLevel.get(levelInMillimeters) ?? 0;

    stationCountsByLevel.set(levelInMillimeters, currentStationCount + 1);
  });

  return Array.from(stationCountsByLevel.values());
};

const getRackLevelDetails = (
  areas: readonly RackingArea[],
  selectedStations: readonly Station[],
): RackLevelDetails => {
  const stationCounts = areas.flatMap((area) =>
    getStationCountsByLevel(getStationsForAreaIds(selectedStations, [area.id])),
  );

  if (stationCounts.length === 0) return {};

  return {
    levelCount: stationCounts.length,
    stationsPerLevel: getRangeValue(stationCounts),
  };
};

const getSingularAreaStationDetails = (
  area: SingularStationArea,
  selectedStations: readonly Station[],
  loadTypeDimensionsById: ReturnType<typeof createLoadTypeDimensionsById>,
): SingularAreaStationDetails => {
  const stations = getStationsForAreaIds(selectedStations, [area.id]);
  const stationsPerLane = getSingularStationCountPerLane(area, loadTypeDimensionsById);
  const stationCount =
    stationsPerLane === undefined ? stations.length : stations.length * stationsPerLane;

  return {
    stationCount,
    laneCount: stations.length,
    stationsPerLane,
  };
};

const createStationSummary = (
  areas: readonly SingularStationArea[],
  selectedStations: readonly Station[],
  loadTypeDimensionsById: ReturnType<typeof createLoadTypeDimensionsById>,
): AreaStationSummary => {
  const stations = getStationsForAreaIds(
    selectedStations,
    areas.map((area) => area.id),
  );
  const areaStationDetails = areas.map((area) =>
    getSingularAreaStationDetails(area, selectedStations, loadTypeDimensionsById),
  );

  return {
    id: 'stations',
    kind: 'station',
    title: getLoadTypeTitle(stations, 'Stations'),
    stationCount: areaStationDetails.reduce((total, details) => total + details.stationCount, 0),
    areaCount: areas.length,
    unitCount: areaStationDetails.reduce((total, details) => total + details.laneCount, 0),
    stationsPerUnit: getOptionalRangeValue(
      areaStationDetails.map((details) => details.stationsPerLane),
    ),
  };
};

const createRackSummary = (
  areas: readonly RackingArea[],
  selectedStations: readonly Station[],
): AreaStationSummary => {
  const stations = getStationsForAreaIds(
    selectedStations,
    areas.map((area) => area.id),
  );
  const { levelCount, stationsPerLevel } = getRackLevelDetails(areas, selectedStations);

  return {
    id: 'rack-stations',
    kind: 'rack',
    title: getLoadTypeTitle(stations, 'Rack stations'),
    stationCount: stations.length,
    areaCount: areas.length,
    unitCount: levelCount,
    stationsPerUnit: stationsPerLevel,
  };
};

const groupSingularAreasByLoadHandlingType = (
  areas: readonly SingularStationArea[],
): Map<SingularStationArea['loadHandlingType'], SingularStationArea[]> => {
  const groupedAreas = new Map<SingularStationArea['loadHandlingType'], SingularStationArea[]>();

  areas.forEach((area) => {
    const existingAreas = groupedAreas.get(area.loadHandlingType);

    if (existingAreas) {
      existingAreas.push(area);
      return;
    }

    groupedAreas.set(area.loadHandlingType, [area]);
  });

  return groupedAreas;
};

const createSingularAreaDetails = (
  areas: readonly SingularStationArea[],
  selectedStations: readonly Station[],
  loadTypeDimensionsById: ReturnType<typeof createLoadTypeDimensionsById>,
): SelectedAreaDetailsGroup[] =>
  Array.from(groupSingularAreasByLoadHandlingType(areas)).map(
    ([loadHandlingType, groupedAreas]) => ({
      id: `singular:${loadHandlingType}`,
      name:
        groupedAreas.length === 1
          ? groupedAreas[0].name
          : getLoadHandlingTypeName(loadHandlingType),
      color: getAreaColorByArea(groupedAreas[0]),
      areaCount: groupedAreas.length,
      summaries: [createStationSummary(groupedAreas, selectedStations, loadTypeDimensionsById)],
    }),
  );

const createRackingAreaDetails = (
  areas: readonly RackingArea[],
  selectedStations: readonly Station[],
): SelectedAreaDetailsGroup[] => {
  if (areas.length === 0) return [];

  return [
    {
      id: 'racking',
      name: areas.length === 1 ? areas[0].name : 'Racking',
      color: getAreaColorByArea(areas[0]),
      areaCount: areas.length,
      summaries: [createRackSummary(areas, selectedStations)],
    },
  ];
};

export const createAreaDetails = (
  selectedAreas: readonly StationArea[],
  selectedStations: readonly Station[],
  loadTypeDimensionsById: ReturnType<typeof createLoadTypeDimensionsById>,
): SelectedAreaDetailsGroup[] => {
  const singularStationAreas: SingularStationArea[] = [];
  const rackingAreas: RackingArea[] = [];

  selectedAreas.forEach((area) => {
    if (isRackingArea(area)) {
      rackingAreas.push(area);
      return;
    }

    singularStationAreas.push(area);
  });

  return [
    ...createSingularAreaDetails(singularStationAreas, selectedStations, loadTypeDimensionsById),
    ...createRackingAreaDetails(rackingAreas, selectedStations),
  ];
};

export const getCombinedStationCount = (areaDetails: readonly SelectedAreaDetailsGroup[]): number =>
  areaDetails.reduce(
    (total, areaDetail) =>
      total +
      areaDetail.summaries.reduce((areaTotal, summary) => areaTotal + summary.stationCount, 0),
    0,
  );
